import { useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { previewCoachingVideoReference } from "@/lib/api";
import { ownedRequest } from "@/features/coach/assist/useAssistContext";
import type { DraftContext } from "@/features/coach-review/composerDrafts";
import type { MatchSlot } from "@/features/matches/matchSlots";
import { readReviewCarry, saveReviewCarry, type ReviewCarry } from "./reviewState";
import { createPrivateRecordingReturn, createPublicRecordingReturn } from "./privateRecordingReturn";
import { decodePrivateFeedbackReviewCarry } from "./privateFeedbackReview";
import { decodeCanonicalFeedbackReviewCarry, type CanonicalReviewCarryFields } from "./canonicalFeedbackReview";
import { loadResourceEditorContinuation } from "@/lib/resourceEditorContinuation";
import { emptyDrawing, type DrawingV2 } from "@/lib/canvas/telestrator-schema";
export function useReviewRecordingReturn(
  owner: DraftContext | null,
  captureId: string,
  matchId: string,
  slot: MatchSlot,
  video: React.RefObject<HTMLVideoElement>,
  drawing?: DrawingV2,
) {
  const carry = (owner ? readReviewCarry(owner, { captureId }) : null) as (ReviewCarry & CanonicalReviewCarryFields) | null;
  const family = carry?.recordingIntent === 'public' ? 'public' : carry?.privateFeedback ? 'private' : null;
  const privateReturn = useRef<{ key: string; controller: ReturnType<typeof createPrivateRecordingReturn> }>();
  const alive = useRef(true);
  const currentSelection = useRef({ matchId, slot, captureId, family, generation: 0 });
  if (currentSelection.current.matchId !== matchId || currentSelection.current.slot !== slot
      || currentSelection.current.captureId !== captureId || currentSelection.current.family !== family) {
    currentSelection.current = { matchId, slot, captureId, family, generation: currentSelection.current.generation + 1 };
  }
  const currentDrawing = useRef(drawing); currentDrawing.current = drawing;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      privateReturn.current?.controller.dispose();
      privateReturn.current = undefined;
    };
  }, []);
  const mutation = useMutation({
    meta: { composerContext: owner },
    retry: false,
    mutationFn: async () => {
      if (
        !alive.current ||
        !owner ||
        !carry ||
        carry.kind !== "feedback" ||
        carry.origin.captureId !== captureId
      )
        throw new Error(
          "Inbox origin unavailable. Return and refresh the private draft.",
        );
      if (family) {
        const key = JSON.stringify([owner, carry.origin, family]);
        if (privateReturn.current?.key !== key) {
          privateReturn.current?.controller.dispose();
          const read = () => readReviewCarry(owner, { captureId, draftId: carry.origin.draftId }) as (ReviewCarry & CanonicalReviewCarryFields) | null;
          const active = () => alive.current && currentSelection.current.captureId === captureId && currentSelection.current.family === family;
          const field = family === 'public' ? 'canonicalFeedback' : 'privateFeedback';
          const write = (value: NonNullable<CanonicalReviewCarryFields['canonicalFeedback']>) => {
            const latest = read();
            if (!latest || JSON.stringify(latest.origin) !== JSON.stringify(carry.origin)
                || (family === 'public' ? latest.recordingIntent !== 'public' : latest.recordingIntent === 'public')) return false;
            if (!saveReviewCarry(owner, { ...latest, [field]: value })) return false;
            return JSON.stringify(read()?.[field]) === JSON.stringify(value);
          };
          const controller = family === 'public'
            ? createPublicRecordingReturn(owner, carry.origin, { active, write, read: () => {
              const latest = read();
              return latest?.kind === 'feedback' && latest.recordingIntent === 'public' && latest.canonicalFeedback
                ? { origin: latest.origin, canonicalFeedback: latest.canonicalFeedback } : null;
            } })
            : createPrivateRecordingReturn(owner, carry.origin, { active, write, read: () => {
              const latest = read();
              return latest?.kind === 'feedback' && latest.recordingIntent !== 'public' && latest.privateFeedback
                ? { origin: latest.origin, privateFeedback: latest.privateFeedback } : null;
            } });
          privateReturn.current = { key, controller };
        }
        const generation = currentSelection.current.generation;
        await privateReturn.current.controller.run({ matchId, slot }, () => {
          const el = video.current;
          if (!el || el.readyState < 1 || !Number.isFinite(el.duration) || el.duration <= 0
              || !Number.isFinite(el.currentTime) || el.currentTime < 0 || el.currentTime > el.duration)
            throw new Error("Wait for the actual recording and select its playhead.");
          return { matchId, slot, timestampSeconds: el.currentTime,
            drawing: structuredClone(currentDrawing.current ?? emptyDrawing()) };
        }, { repairRejected: privateReturn.current.controller.getState().rejected,
          isCurrent: () => currentSelection.current.generation === generation });
        return;
      }
      const el = video.current;
      if (
        !el ||
        el.readyState < 1 ||
        !Number.isFinite(el.duration) ||
        el.duration <= 0 ||
        !Number.isFinite(el.currentTime) ||
        el.currentTime < 0 ||
        el.currentTime > el.duration
      )
        throw new Error(
          "Wait for the actual recording and select its playhead.",
        );
      const reference = await ownedRequest(owner, () =>
        previewCoachingVideoReference(
          carry.origin.draftId,
          {
            match_id: matchId,
            slot,
            timestamp_seconds: el.currentTime,
            expected_draft_revision: carry.origin.draftRevision,
            confirmation_id: carry.origin.confirmationId,
            expected_confirmation_revision: carry.origin.confirmationRevision,
          },
          { teamId: owner.teamId, seasonId: owner.seasonId },
        ),
      );
      if (
        !alive.current ||
        currentSelection.current.matchId !== matchId ||
        currentSelection.current.slot !== slot
      )
        throw new Error(
          "Recording selection changed. Review the current recording again.",
        );
      const current = readReviewCarry(owner, { captureId });
      if (
        !current ||
        current.kind !== "feedback" ||
        JSON.stringify(current.video) !== JSON.stringify(carry.video) ||
        JSON.stringify(current.origin) !== JSON.stringify(carry.origin)
      )
        throw new Error("Inbox target changed. Return to review again.");
      saveReviewCarry(owner, { ...current, video: reference });
    },
  });
  let savedResource = false, pendingSave = false;
  const writing = family === 'public' ? carry?.canonicalFeedback : carry?.privateFeedback;
  const decode = family === 'public' ? decodeCanonicalFeedbackReviewCarry : decodePrivateFeedbackReviewCarry;
  if (owner && writing?.recording_entry_id && decode(writing)) {
    try {
      const row = loadResourceEditorContinuation(owner, writing.recording_entry_id);
      if (row?.return_target.kind === 'assist' && JSON.stringify(row.return_target.origin) === JSON.stringify(carry?.origin)
          && row.return_target.approval_family === (family === 'public' ? 'public' : undefined)) {
        savedResource = !!row.accepted; pendingSave = !!row.pending;
      }
    } catch { /* Explicit action reports inaccessible original storage; never dispatch on render. */ }
  }
  return { ...mutation, privateRecording: family === 'private', independentRecording: !!family, savedResource, pendingSave,
    rejectedSave: privateReturn.current?.controller.getState().rejected ?? false };
}
